import { createContext, useState, useEffect, ReactNode } from "react";
import { useLanguage } from "@/shared/hooks/use-language";
import { effectiveNumberFormat, isNumberFormat, type NumberFormat } from "@/shared/lib/user-setting";

const CHOSEN_NUMBER_FORMAT_STORAGE_KEY = "chosenNumberFormat";

interface NumberFormatContextType {
  numberFormat: NumberFormat;
  chosenNumberFormat: NumberFormat | null;
  setNumberFormat: (format: NumberFormat | null) => void;
}

export const NumberFormatContext = createContext<NumberFormatContextType | undefined>(
  undefined
);

export const NumberFormatProvider = ({ children }: { children: ReactNode }) => {
  const { language } = useLanguage();
  const [chosenNumberFormat, setChosenNumberFormat] = useState<NumberFormat | null>(() => {
    const stored = localStorage.getItem(CHOSEN_NUMBER_FORMAT_STORAGE_KEY);
    return isNumberFormat(stored) ? stored : null;
  });

  useEffect(() => {
    if (chosenNumberFormat) {
      localStorage.setItem(CHOSEN_NUMBER_FORMAT_STORAGE_KEY, chosenNumberFormat);
    } else {
      localStorage.removeItem(CHOSEN_NUMBER_FORMAT_STORAGE_KEY);
    }
  }, [chosenNumberFormat]);

  const numberFormat = effectiveNumberFormat(chosenNumberFormat, language);

  const setNumberFormat = (format: NumberFormat | null) => {
    setChosenNumberFormat(format);
  };

  return (
    <NumberFormatContext.Provider value={{ numberFormat, chosenNumberFormat, setNumberFormat }}>
      {children}
    </NumberFormatContext.Provider>
  );
};
